// Aktív-hozzárendelés FIGYELMEZTETÉS (nem-blokkoló) — a legacy checkForActiveAssignment modal 1:1: ha már van
// aktív hozzárendelés ugyanarra a szolgáltatásra ugyanannál az ügyfélnél/fióknál, mentés ELŐTT figyelmeztet.
// HÁROM akció: „Meglévő megtekintése" (visszatér a listához) / „Mentés folytatása" (nem tiltás!) / „Mégsem".
// A tulajdonos/szolgáltatás nevét a BFF adja (generikus, jog-nevet nem szivárogtat).
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ActiveAssignmentExisting {
  id: string;
  serviceName: string | null;
  ownerName: string | null;
  /** A találat HELYE: önálló hozzárendelés, vagy egy MÁSIK hozzárendelés komponense (ekkor az `id` a szülő). */
  matchedIn: 'root' | 'component';
  /** A form melyik eleme ütközött: a fő szolgáltatás, vagy egy felvett komponens. */
  matchedFrom: 'service' | 'component';
}

export interface ServiceAssignmentActiveWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: ActiveAssignmentExisting | null;
  /** „Mentés folytatása" — a mentés nem blokkolt, csak megerősítendő. */
  onProceed: () => void;
  /** „Meglévő megtekintése" — visszatér a listához (a meglévő ott megtalálható). */
  onViewExisting: () => void;
  pending?: boolean;
}

/** A figyelmeztető szöveg kulcsa a találat helye/forrása szerint (lásd a komponensben a magyarázatot). */
function descKey(existing: ActiveAssignmentExisting | null): string {
  if (existing?.matchedIn === 'component')
    return 'serviceAssignments.activeWarning.descInComponent';
  if (existing?.matchedFrom === 'component') {
    return 'serviceAssignments.activeWarning.descFromComponent';
  }
  return 'serviceAssignments.activeWarning.desc';
}

export function ServiceAssignmentActiveWarningDialog({
  open,
  onOpenChange,
  existing,
  onProceed,
  onViewExisting,
  pending = false,
}: ServiceAssignmentActiveWarningDialogProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('serviceAssignments.activeWarning.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex gap-2.5">
            <ExclamationTriangleIcon
              className="text-warning mt-0.5 size-5 shrink-0"
              aria-hidden="true"
            />
            <DialogDescription>
              {/*
                A szöveg MEGMONDJA, MIÉRT szólt — különben egy komponens-találat úgy néz ki, mint egy hibás
                figyelmeztetés („de hát erre nincs is hozzárendelés!"), és a felhasználó megtanulja átlapozni.
                Két külön eset: (a) a MEGLÉVŐ sor egy másik hozzárendelés komponense, (b) a FORMBAN felvett
                komponens szolgáltatása ütközik. Prioritás: (a) — az a nehezebben kitalálható.
              */}
              {t(descKey(existing), {
                service: existing?.serviceName ?? '',
                owner: existing?.ownerName ?? '',
              })}
            </DialogDescription>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('serviceAssignments.activeWarning.cancel')}
          </Button>
          <Button type="button" variant="outline" onClick={onViewExisting} disabled={pending}>
            {t('serviceAssignments.activeWarning.viewExisting')}
          </Button>
          <Button type="button" variant="warning" onClick={onProceed} disabled={pending}>
            {t('serviceAssignments.activeWarning.proceed')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
